import {
  AfterNowRelativeExactTimeframeCondition,
  AfterTimeframeCondition,
  AllTimeTimeframeCondition,
  AnnualTimeframeCondition,
  AutomationStartTimeframeCondition,
  BeforeNowRelativeExactTimeframeCondition,
  BeforeNowRelativeTimeframeCondition,
  BeforeRelativeTimeframeCondition,
  BeforeTimeframeCondition,
  BetweenDatesTimeframeCondition,
  BetweenRelativeTimeframeCondition,
  FullDateTimeframeCondition,
  OnTimeframeCondition,
  ThisMonthTimeframeCondition,
  ThisWeekTimeframeCondition,
  TimeframeConditionType,
  TodayTimeframeCondition,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-timeframe";
import {
  ActivityCount,
  CustomerAttributeCondition,
  CustomerCharacteristicType,
  CustomEventCondition,
  isConstantActivityCount,
  Segment,
  SegmentCondition,
  SegmentConditionBlock,
  SegmentConditionType,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import {
  ComparisonType,
  tokenHierarchyPrerequisiteCounts,
  WhereCondition,
  WhereConditionDataType,
} from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import {
  BooleanCondition,
  CityProximityCondition,
  TokenCondition,
  TokenHierarchyCondition,
  TokenListCondition,
} from "@redotech/redo-customer-segments-common/segments/segment-where-condition-zod-schema";
import { isPropertyNameValid } from "@redotech/redo-public-common/custom-events/create-custom-event";
import { assertNever } from "@redotech/util/type";
import {
  ActivityCountInProgress,
  AnnualTimeframeConditionInProgress,
  CustomerActivityConditionInProgress,
  CustomerAttributeConditionInProgress,
  CustomEventConditionInProgress,
  FullDateTimeFrameConditionInProgress,
  NumericConditionInProgress,
  SegmentConditionBlockInProgress,
  SegmentConditionInProgress,
  SegmentInProgress,
  StringConditionInProgress,
  WhereConditionInProgress,
} from "./segment-in-progress";

export function isSegmentValid(segment: SegmentInProgress): segment is Segment {
  const nameValid = segment.name.trim().length > 0;

  if (segment.type === "static") {
    return nameValid;
  }

  const nonzeroAmountOfBlocks = segment.conditionBlocks.length > 0;
  const conditionsValid = segment.conditionBlocks.every(
    isSegmentConditionBlockValid,
  );
  return nameValid && conditionsValid && nonzeroAmountOfBlocks;
}

export function isSegmentConditionBlockValid(
  segmentConditionBlock: SegmentConditionBlockInProgress,
): segmentConditionBlock is SegmentConditionBlock {
  return (
    segmentConditionBlock.conditions.length > 0 &&
    segmentConditionBlock.conditions.every(isSegmentConditionValid)
  );
}

export function isSegmentConditionValid(
  segmentCondition: SegmentConditionInProgress,
): segmentCondition is SegmentCondition {
  switch (segmentCondition.type) {
    case "empty":
      return false;
    case SegmentConditionType.CUSTOMER_ATTRIBUTE:
      return isCustomerAttributeConditionValid(segmentCondition);
    case SegmentConditionType.CUSTOMER_ACTIVITY:
      return isCustomerActivityConditionValid(segmentCondition);
    case SegmentConditionType.CUSTOM_EVENT:
      return isCustomEventConditionValid(segmentCondition);
  }
}

function isCustomerAttributeConditionValid(
  customerAttributeCondition: CustomerAttributeConditionInProgress,
): customerAttributeCondition is CustomerAttributeCondition {
  return (
    !!customerAttributeCondition.whereCondition &&
    isWhereConditionValid(customerAttributeCondition.whereCondition)
  );
}

function isWhereConditionValid(
  whereCondition: WhereConditionInProgress<string>,
): whereCondition is WhereCondition<string> {
  switch (whereCondition.type) {
    case WhereConditionDataType.TOKEN:
      return isTokenConditionValid(whereCondition);
    case WhereConditionDataType.NUMERIC:
      return isNumericConditionValid(whereCondition);
    case WhereConditionDataType.BOOLEAN:
      return isBooleanConditionValid(whereCondition);
    case WhereConditionDataType.DATE_FULL:
      return isFullDateTimeFrameValid(whereCondition.comparison);
    case WhereConditionDataType.DATE_ANNUAL:
      return isAnnualTimeframeConditionValid(whereCondition.comparison);
    case WhereConditionDataType.TOKEN_LIST:
      return isTokenListConditionValid(whereCondition);
    case WhereConditionDataType.TOKEN_HIERARCHY:
      return isTokenHierarchyConditionValid(whereCondition);
    case WhereConditionDataType.CITY_PROXIMITY:
      return isCityProximityConditionValid(whereCondition);
    case WhereConditionDataType.CUSTOM_FIELD:
      return isCustomFieldConditionValid(whereCondition);
    case WhereConditionDataType.STRING:
      return isStringConditionValid(whereCondition);
    default:
      assertNever(whereCondition);
  }
}

function isTokenConditionValid(condition: TokenCondition<string>): boolean {
  return condition.comparison.values.length > 0;
}

function isNumericConditionValid(
  condition: NumericConditionInProgress<string>,
): boolean {
  return condition.comparison.value !== undefined;
}

function isBooleanConditionValid(condition: BooleanCondition<string>): boolean {
  return true;
}

function isTokenListConditionValid(
  condition: TokenListCondition<string>,
): boolean {
  return condition.comparison.values.length > 0;
}

function isTokenHierarchyConditionValid(
  condition: TokenHierarchyCondition<string> | CityProximityCondition<string>,
): boolean {
  const prerequisiteCount =
    tokenHierarchyPrerequisiteCounts[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      condition.dimension as CustomerCharacteristicType
    ];
  if (!prerequisiteCount) {
    throw new Error(
      `No prerequisite keys found for dimension ${condition.dimension}`,
    );
  }
  return "values" in condition.comparison
    ? condition.comparison.values.length > 0
    : !!condition.comparison.value &&
        condition.comparison.prerequisiteValues.length === prerequisiteCount &&
        condition.comparison.prerequisiteValues.every((value) => !!value);
}

function isCityProximityConditionValid(
  condition: CityProximityCondition<string>,
): boolean {
  return (
    isTokenHierarchyConditionValid(condition) &&
    condition.comparison.options.radius > 0 &&
    !!condition.comparison.options.units
  );
}

function isCustomFieldConditionValid(
  condition: WhereConditionInProgress<string>,
): boolean {
  if (condition.type !== WhereConditionDataType.CUSTOM_FIELD) {
    return false;
  }

  const isIdValid =
    !!condition.declarationId && condition.declarationId.trim().length > 0;
  if (!isIdValid) {
    return false;
  }
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  // with some refactoring, could share validation code with non-custom field conditions,
  // but it's kept separate right now since the logic is simple enough to not benefit from it very much yet.
  switch (condition.comparison.type) {
    case ComparisonType.TOKEN:
      return isIdValid && condition.comparison.values.length > 0;
    case ComparisonType.NUMERIC:
      return isIdValid && condition.comparison.value !== undefined;
    case ComparisonType.BOOLEAN:
      return isIdValid;
    case ComparisonType.EXISTS:
      return isIdValid;
    default:
      // TypeScript can't narrow through the indexed access type, but after
      // eliminating the cases above the only remaining type is the date comparison.
      return (
        isIdValid &&
        isFullDateTimeFrameValid(
          condition.comparison as FullDateTimeFrameConditionInProgress<FullDateTimeframeCondition>,
        )
      );
  }
}

function isStringConditionValid(
  condition: StringConditionInProgress<string>,
): boolean {
  return (
    condition.comparison.value !== undefined &&
    condition.comparison.value.trim().length > 0
  );
}

function isCustomerActivityConditionValid(
  customerActivityCondition: CustomerActivityConditionInProgress,
): customerActivityCondition is CustomerActivityConditionInProgress {
  const eventTypeValid = !!customerActivityCondition.activityType;
  const countValid = isActivityCountValid(customerActivityCondition.count);
  const timeFrameValid = isFullDateTimeFrameValid(
    customerActivityCondition.timeframe,
  );
  const whereConditionsValid = customerActivityCondition.whereConditions.every(
    isWhereConditionValid,
  );
  return eventTypeValid && countValid && timeFrameValid && whereConditionsValid;
}

function isCustomEventConditionValid(
  condition: CustomEventConditionInProgress,
): condition is CustomEventCondition & CustomEventConditionInProgress {
  const eventNameValid =
    !!condition.eventName && condition.eventName.length > 0;
  const countValid = isActivityCountValid(condition.count);
  const timeFrameValid = isFullDateTimeFrameValid(condition.timeframe);
  const propertyFiltersValid = condition.propertyFilters.every(
    (filter) =>
      isWhereConditionValid(filter) && isPropertyNameValid(filter.dimension),
  );
  return eventNameValid && countValid && timeFrameValid && propertyFiltersValid;
}

function isFullDateTimeFrameValid(
  timeFrame: FullDateTimeFrameConditionInProgress<FullDateTimeframeCondition>,
): timeFrame is FullDateTimeframeCondition {
  switch (timeFrame.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return isBeforeNowRelativeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.ON:
      return isOnTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BEFORE_RELATIVE:
      return isBeforeRelativeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BEFORE:
      return isBeforeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.AFTER:
      return isAfterTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.ALL_TIME:
      return isAllTimeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BETWEEN_RELATIVE:
      return isBetweenRelativeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BETWEEN_DATES:
      return isBetweenDatesTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.TODAY:
      return isTodayTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.AUTOMATION_START:
      return isAutomationStartConditionValid(timeFrame);
    default:
      assertNever(timeFrame);
  }
}
function isAnnualTimeframeConditionValid(
  timeFrame: AnnualTimeframeConditionInProgress<AnnualTimeframeCondition>,
): timeFrame is AnnualTimeframeCondition {
  switch (timeFrame.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return isBeforeNowRelativeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.ON:
      return isOnTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BEFORE:
      return isBeforeTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.AFTER:
      return isAfterTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BETWEEN_DATES:
      return isBetweenDatesTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT:
      return isBeforeNowRelativeExactTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT:
      return isAfterNowRelativeExactTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.TODAY:
      return isTodayTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.THIS_WEEK:
      return isThisWeekTimeframeConditionValid(timeFrame);
    case TimeframeConditionType.THIS_MONTH:
      return isThisMonthTimeframeConditionValid(timeFrame);
    default:
      assertNever(timeFrame);
  }
}
function isBeforeNowRelativeTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<BeforeNowRelativeTimeframeCondition>,
): condition is BeforeNowRelativeTimeframeCondition {
  return condition.value !== undefined && condition.units !== undefined;
}

function isOnTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<OnTimeframeCondition>,
): condition is OnTimeframeCondition {
  return condition.date !== undefined;
}

function isBeforeRelativeTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<BeforeRelativeTimeframeCondition>,
): condition is BeforeRelativeTimeframeCondition {
  return condition.value !== undefined && condition.units !== undefined;
}

function isBeforeTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<BeforeTimeframeCondition>,
): condition is BeforeTimeframeCondition {
  return condition.date !== undefined;
}

function isAfterTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<AfterTimeframeCondition>,
): condition is AfterTimeframeCondition {
  return condition.date !== undefined;
}

function isAllTimeTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<AllTimeTimeframeCondition>,
): condition is AllTimeTimeframeCondition {
  return true;
}

function isTodayTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<TodayTimeframeCondition>,
): condition is TodayTimeframeCondition {
  return true;
}

function isAutomationStartConditionValid(
  condition: FullDateTimeFrameConditionInProgress<AutomationStartTimeframeCondition>,
): condition is AutomationStartTimeframeCondition {
  return true;
}

function isBeforeNowRelativeExactTimeframeConditionValid(
  condition: AnnualTimeframeConditionInProgress<BeforeNowRelativeExactTimeframeCondition>,
): condition is BeforeNowRelativeExactTimeframeCondition {
  return condition.value !== undefined && condition.units !== undefined;
}

function isAfterNowRelativeExactTimeframeConditionValid(
  condition: AnnualTimeframeConditionInProgress<AfterNowRelativeExactTimeframeCondition>,
): condition is AfterNowRelativeExactTimeframeCondition {
  return condition.value !== undefined && condition.units !== undefined;
}

function isThisWeekTimeframeConditionValid(
  condition: AnnualTimeframeConditionInProgress<ThisWeekTimeframeCondition>,
): condition is ThisWeekTimeframeCondition {
  return true;
}

function isThisMonthTimeframeConditionValid(
  condition: AnnualTimeframeConditionInProgress<ThisMonthTimeframeCondition>,
): condition is ThisMonthTimeframeCondition {
  return true;
}

function isBetweenRelativeTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<BetweenRelativeTimeframeCondition>,
): condition is BetweenRelativeTimeframeCondition {
  return (
    condition.start !== undefined &&
    condition.end !== undefined &&
    condition.units !== undefined
  );
}

function isBetweenDatesTimeframeConditionValid(
  condition: FullDateTimeFrameConditionInProgress<BetweenDatesTimeframeCondition>,
): condition is BetweenDatesTimeframeCondition {
  return condition.range !== undefined;
}

function isActivityCountValid(
  activityCount: ActivityCountInProgress,
): activityCount is ActivityCount {
  if (isConstantActivityCount(activityCount)) {
    return true;
  } else {
    return activityCount.n !== undefined;
  }
}
